import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Table, Badge, Alert, Card, Row, Col } from 'react-bootstrap';
import VodService from '../../../services/VodService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const DAYS_OF_WEEK = [
  { label: 'Sun', value: 0 },
  { label: 'Mon', value: 1 },
  { label: 'Tue', value: 2 },
  { label: 'Wed', value: 3 },
  { label: 'Thu', value: 4 },
  { label: 'Fri', value: 5 },
  { label: 'Sat', value: 6 },
];

const defaultForm = {
  playlist_id: '',
  stream_id: '',
  start_date: '',
  end_date: '',
  start_time: '',
  end_time: '',
  days_of_week: [0, 1, 2, 3, 4, 5, 6],
  loop_enabled: false,
  is_active: true,
  failover_enabled: true,
};

const ScheduleManagement = () => {
  const [schedules, setSchedules] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [streams, setStreams] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(defaultForm);

  useEffect(() => {
    fetchSchedules();
    fetchPlaylists();
    fetchStreams();
  }, []);

  const fetchSchedules = async () => {
    try {
      setLoading(true);
      const response = await VodService.getAllSchedules();
      if (response.success) {
        setSchedules(response.schedules || []);
      }
    } catch (error) {
      toast.error('Failed to fetch schedules');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlaylists = async () => {
    try {
      const response = await VodService.getAllPlaylists();
      if (response.success) {
        setPlaylists(response.playlists || []);
      }
    } catch (error) {
      console.error('Failed to fetch playlists:', error);
    }
  };

  const fetchStreams = async () => {
    try {
      const response = await VodService.getAllStreams();
      if (response.success) {
        setStreams(response.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch streams:', error);
    }
  };

  const handleDayToggle = (dayValue) => {
    const days = [...formData.days_of_week];
    const idx = days.indexOf(dayValue);
    if (idx > -1) {
      days.splice(idx, 1);
    } else {
      days.push(dayValue);
      days.sort();
    }
    setFormData({ ...formData, days_of_week: days });
  };

  const handleCreateSchedule = async (e) => {
    e.preventDefault();
    if (!formData.playlist_id || !formData.stream_id) {
      toast.warning('Please select both a playlist and a stream');
      return;
    }
    if (!formData.start_time || !formData.end_time) {
      toast.warning('Please set start and end times');
      return;
    }
    if (formData.days_of_week.length === 0) {
      toast.warning('Please select at least one day of the week');
      return;
    }
    try {
      setLoading(true);
      const payload = {
        ...formData,
        start_time: formData.start_date
          ? `${formData.start_date}T${formData.start_time}`
          : formData.start_time,
        end_time: formData.end_date
          ? `${formData.end_date}T${formData.end_time}`
          : formData.end_time,
      };
      const response = await VodService.createSchedule(payload);
      if (response.success) {
        toast.success('Schedule created successfully');
        setShowCreateModal(false);
        setFormData(defaultForm);
        fetchSchedules();
      }
    } catch (error) {
      toast.error('Failed to create schedule');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSchedule = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = {
        ...formData,
        start_time: formData.start_date
          ? `${formData.start_date}T${formData.start_time}`
          : formData.start_time,
        end_time: formData.end_date
          ? `${formData.end_date}T${formData.end_time}`
          : formData.end_time,
      };
      const response = await VodService.updateSchedule(selectedSchedule.id, payload);
      if (response.success) {
        toast.success('Schedule updated successfully');
        setShowEditModal(false);
        setSelectedSchedule(null);
        setFormData(defaultForm);
        fetchSchedules();
      }
    } catch (error) {
      toast.error('Failed to update schedule');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSchedule = async (id) => {
    if (!window.confirm('Are you sure you want to delete this schedule?')) return;
    try {
      setLoading(true);
      const response = await VodService.deleteSchedule(id);
      if (response.success) {
        toast.success('Schedule deleted successfully');
        fetchSchedules();
      }
    } catch (error) {
      toast.error('Failed to delete schedule');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAutomation = async (schedule) => {
    try {
      const newState = !schedule.is_active;
      await VodService.toggleAutomation(schedule.id, newState);
      toast.success(`Automation ${newState ? 'enabled' : 'disabled'}`);
      fetchSchedules();
    } catch (error) {
      toast.error('Failed to toggle automation');
      console.error(error);
    }
  };

  const openEditModal = (schedule) => {
    setSelectedSchedule(schedule);
    // Parse start/end times
    let startDate = '', startTime = '', endDate = '', endTime = '';
    if (schedule.start_time) {
      const parts = schedule.start_time.split('T');
      if (parts.length === 2) { startDate = parts[0]; startTime = parts[1].slice(0, 5); }
      else { startTime = schedule.start_time.slice(0, 5); }
    }
    if (schedule.end_time) {
      const parts = schedule.end_time.split('T');
      if (parts.length === 2) { endDate = parts[0]; endTime = parts[1].slice(0, 5); }
      else { endTime = schedule.end_time.slice(0, 5); }
    }
    setFormData({
      playlist_id: schedule.playlist_id || '',
      stream_id: schedule.stream_id || '',
      start_date: startDate,
      end_date: endDate,
      start_time: startTime,
      end_time: endTime,
      days_of_week: Array.isArray(schedule.days_of_week)
        ? schedule.days_of_week
        : JSON.parse(schedule.days_of_week || '[0,1,2,3,4,5,6]'),
      loop_enabled: !!schedule.loop_enabled,
      is_active: !!schedule.is_active,
      failover_enabled: !!schedule.failover_enabled,
    });
    setShowEditModal(true);
  };

  const formatScheduleTime = (timeStr) => {
    if (!timeStr) return '-';
    if (timeStr.includes('T')) {
      const [date, time] = timeStr.split('T');
      return `${date} ${time.slice(0, 5)}`;
    }
    return timeStr.slice(0, 5);
  };

  const renderDayBadges = (days) => {
    const daysArr = Array.isArray(days) ? days : [];
    return DAYS_OF_WEEK.map(d => (
      <Badge
        key={d.value}
        variant={daysArr.includes(d.value) ? 'primary' : 'secondary'}
        className="mr-1"
        style={{ opacity: daysArr.includes(d.value) ? 1 : 0.3 }}
      >
        {d.label}
      </Badge>
    ));
  };

  const ScheduleFormBody = () => (
    <>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Playlist *</Form.Label>
            <Form.Select
              value={formData.playlist_id}
              onChange={(e) => setFormData({ ...formData, playlist_id: e.target.value })}
              required
            >
              <option value="">Select a playlist...</option>
              {playlists.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Stream *</Form.Label>
            <Form.Select
              value={formData.stream_id}
              onChange={(e) => setFormData({ ...formData, stream_id: e.target.value })}
              required
            >
              <option value="">Select a stream...</option>
              {streams.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Start Date</Form.Label>
            <Form.Control
              type="date"
              value={formData.start_date}
              onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
            />
            <Form.Text className="text-muted">Leave empty for recurring schedule</Form.Text>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>End Date</Form.Label>
            <Form.Control
              type="date"
              value={formData.end_date}
              onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
            />
            <Form.Text className="text-muted">Leave empty for no end date</Form.Text>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Start Time *</Form.Label>
            <Form.Control
              type="time"
              value={formData.start_time}
              onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
              required
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>End Time *</Form.Label>
            <Form.Control
              type="time"
              value={formData.end_time}
              onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
              required
            />
          </Form.Group>
        </Col>
      </Row>

      <Form.Group className="mb-3">
        <Form.Label>Days of Week</Form.Label>
        <div className="d-flex flex-wrap gap-2">
          {DAYS_OF_WEEK.map(day => (
            <Button
              key={day.value}
              type="button"
              variant={formData.days_of_week.includes(day.value) ? 'primary' : 'outline-secondary'}
              size="sm"
              onClick={() => handleDayToggle(day.value)}
              style={{ minWidth: '50px' }}
            >
              {day.label}
            </Button>
          ))}
        </div>
      </Form.Group>

      <Row>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="loop-switch"
              label="Loop Playlist"
              checked={formData.loop_enabled}
              onChange={(e) => setFormData({ ...formData, loop_enabled: e.target.checked })}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="active-switch"
              label="Active"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="failover-switch"
              label="Failover Enabled"
              checked={formData.failover_enabled}
              onChange={(e) => setFormData({ ...formData, failover_enabled: e.target.checked })}
            />
          </Form.Group>
        </Col>
      </Row>
    </>
  );

  return (
    <div className="schedule-management">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Schedule Management</h2>
        <Button variant="primary" onClick={() => { setFormData(defaultForm); setShowCreateModal(true); }}>
          <i className="flaticon-plus mr-2"></i> Create Schedule
        </Button>
      </div>

      <Alert variant="info">
        <i className="flaticon-information mr-2"></i>
        Schedules allow you to automatically broadcast a playlist on a stream at specific times. Use the automation toggle to enable/disable without deleting.
      </Alert>

      {schedules.length === 0 && !loading ? (
        <div className="text-center py-5">
          <i className="flaticon-calendar" style={{ fontSize: '48px', color: '#ccc' }}></i>
          <p className="mt-3 text-muted">No schedules found. Create your first schedule to automate broadcasting.</p>
        </div>
      ) : (
        <Card>
          <Card.Body className="p-0">
            <Table responsive hover className="mb-0">
              <thead className="thead-light">
                <tr>
                  <th>Playlist</th>
                  <th>Stream</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Days</th>
                  <th>Loop</th>
                  <th>Automation</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {schedules.map((schedule) => (
                  <tr key={schedule.id}>
                    <td>
                      <strong>{schedule.playlist_name || `Playlist #${schedule.playlist_id}`}</strong>
                    </td>
                    <td>{schedule.stream_name || `Stream #${schedule.stream_id}`}</td>
                    <td>{formatScheduleTime(schedule.start_time)}</td>
                    <td>{formatScheduleTime(schedule.end_time)}</td>
                    <td>
                      <div className="d-flex flex-wrap">
                        {renderDayBadges(
                          Array.isArray(schedule.days_of_week)
                            ? schedule.days_of_week
                            : JSON.parse(schedule.days_of_week || '[]')
                        )}
                      </div>
                    </td>
                    <td>
                      {schedule.loop_enabled ? (
                        <Badge variant="success">On</Badge>
                      ) : (
                        <Badge variant="secondary">Off</Badge>
                      )}
                    </td>
                    <td>
                      <Form.Check
                        type="switch"
                        id={`automation-${schedule.id}`}
                        checked={!!schedule.is_active}
                        onChange={() => handleToggleAutomation(schedule)}
                        label={schedule.is_active ? 'ON' : 'OFF'}
                      />
                    </td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="mr-2"
                        onClick={() => openEditModal(schedule)}
                      >
                        <i className="flaticon-edit"></i>
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDeleteSchedule(schedule.id)}
                      >
                        <i className="flaticon-delete"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {/* Create Schedule Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create Schedule</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateSchedule}>
          <Modal.Body>
            <ScheduleFormBody />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? 'Creating...' : 'Create Schedule'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Edit Schedule Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Schedule</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUpdateSchedule}>
          <Modal.Body>
            <ScheduleFormBody />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? 'Updating...' : 'Update Schedule'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default ScheduleManagement;
